import { useState, useMemo } from 'react'

type Order = 'asc' | 'desc'

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split('.').reduce((acc: unknown, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
}

function compare<T>(a: T, b: T, orderBy: string): number {
  const aVal = getNestedValue(a, orderBy)
  const bVal = getNestedValue(b, orderBy)

  if (aVal == null && bVal == null) return 0
  if (aVal == null) return 1
  if (bVal == null) return -1

  if (typeof aVal === 'string' && typeof bVal === 'string') {
    return aVal.localeCompare(bVal, undefined, { sensitivity: 'base' })
  }
  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return aVal - bVal
  }
  if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
    return (aVal === bVal) ? 0 : aVal ? -1 : 1
  }

  return String(aVal).localeCompare(String(bVal))
}

export function useSortable<T>(items: T[], defaultOrderBy: string, defaultOrder: Order = 'asc') {
  const [order, setOrder] = useState<Order>(defaultOrder)
  const [orderBy, setOrderBy] = useState(defaultOrderBy)

  const sorted = useMemo(() => {
    const multiplier = order === 'asc' ? 1 : -1
    return [...items].sort((a, b) => multiplier * compare(a, b, orderBy))
  }, [items, order, orderBy])

  function handleSort(property: string) {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  return { sorted, order, orderBy, handleSort }
}
